import { forwardRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeftRight, Droplets, LayoutDashboard, Wallet, Menu, X, History, Settings, Activity, TrendingUp, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAccount, useConnect, useDisconnect, useConnectors } from 'wagmi'
import { toast } from "sonner"
import { NetworkSelector } from "@/components/web3/NetworkSelector";

const navItems = [
  { path: "/swap", label: "Swap", icon: ArrowLeftRight },
  { path: "/pools", label: "Pools", icon: Droplets },
  { path: "/markets", label: "Markets", icon: TrendingUp },
  { path: "/portfolio", label: "Portfolio", icon: LayoutDashboard },
  { path: "/history", label: "History", icon: History },
];

const secondaryNavItems = [
  { path: "/docs", label: "Docs", icon: BookOpen },
  { path: "/settings", label: "Settings", icon: Settings },
  { path: "/status", label: "Status", icon: Activity },
];


export const Header = forwardRef<HTMLElement>((_, ref) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { address, isConnected } = useAccount();
  const { connect } = useConnect();
  const { disconnect } = useDisconnect();
  const connectors = useConnectors();

  const handleConnect = () => {
    if (isConnected) {
      disconnect();
    } else {
      // Only use injected connector if a wallet provider is actually present
      // (incognito mode and mobile browsers don't have window.ethereum)
      const hasInjectedProvider = typeof window !== 'undefined' && !!(window as any).ethereum;
      if (hasInjectedProvider) {
        const injectedConnector = connectors.find(c => c.type === 'injected');
        if (injectedConnector) {
          connect({ connector: injectedConnector });
          return;
        }
      }
      // Fall back to WalletConnect (mobile deep links / QR code)
      const wcConnector = connectors.find(c => c.type === 'walletConnect');
      if (wcConnector) {
        connect({ connector: wcConnector });
        return;
      }
      // No usable connector — guide the user
      console.warn('[FluxSwap] No usable wallet connector. Available:', connectors.map(c => c.type));
      toast.error("No wallet detected. Use WalletConnect or install MetaMask.");
    }
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  }

  return (
    <header ref={ref} className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative h-9 w-9 rounded-lg bg-gradient-to-br from-primary via-[hsl(280,80%,60%)] to-[hsl(320,70%,55%)] p-[2px]">
              <div className="flex h-full w-full items-center justify-center rounded-[6px] bg-background">
                <span className="text-lg font-bold gradient-text">F</span>
              </div>
            </div>
            <span className="text-xl font-bold tracking-tight">
              Flux<span className="gradient-text">Swap</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Network Selector - Now real component */}
            <div className="hidden sm:block">
              <NetworkSelector />
            </div>

            {/* Connect Wallet Button */}
            <Button
              variant={isConnected ? "outline" : "gradient"}
              size="default"
              onClick={handleConnect}
              className="hidden sm:flex"
            >
              <Wallet className="h-4 w-4" />
              {isConnected && address ? formatAddress(address) : "Connect Wallet"}
            </Button>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-secondary rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 glass-strong border-b border-border/50 animate-slide-up">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all",
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
            {/* Secondary Nav Items */}
            <div className="pt-2 border-t border-border/50 mt-2 flex flex-col gap-2">
              {secondaryNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all",
                      isActive
                        ? "bg-secondary text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <div className="pt-2 border-t border-border/50 mt-2">
              <Button
                variant={isConnected ? "outline" : "gradient"}
                size="lg"
                onClick={handleConnect}
                className="w-full"
              >
                <Wallet className="h-4 w-4" />
                {isConnected && address ? formatAddress(address) : "Connect Wallet"}
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
});

Header.displayName = "Header";
